import { useEffect, useCallback, useState } from "react";
import {
  subscribeAppVersion,
  unsubscribeAppVersion,
} from "../api/version.subscription";
import packageJson from "../../../../package.json";
import { useGetAppVersionQuery } from "../api/version.api";
import { AppVersion } from "../../../types/index";
import { VersionCheckerResult } from "../types/index";

/**
 * A custom hook that manages version checking and updates for the application.
 *
 * @returns {Object} An object containing:
 * @returns {string} currentversion - The current version from package.json
 * @returns {string} version - The latest version from the server
 * @returns {boolean} isLoading - Whether the version check is loading
 * @returns {boolean} isFetching - Whether a version fetch is in progress
 */
export const useVersionChecker = (): VersionCheckerResult => {
  const currentversion: string = packageJson.version;
  const [version, setVersion] = useState<string>(currentversion);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { data, isLoading, isFetching, refetch } = useGetAppVersionQuery(
    null,
    {
      refetchOnReconnect: true,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const [networkStatus, setNetworkStatus] = useState<'online' | 'offline'>(
    window.navigator.onLine ? "online" : "offline"
  );
  
  console.log({ networkStatus });
  // Add event listeners for network status
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus("online");
      refetch();
    };
    const handleOffline = () => setNetworkStatus("offline");

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  console.log({ d:data?.version_number, isLoading, isFetching });

  useEffect(() => {
    if (data?.version_number) {
      setVersion(data.version_number);
    }
  }, [data]);

  useEffect(() => {
    if (
      currentversion.localeCompare(version, undefined, {
        numeric: true,
        sensitivity: "base",
      }) >= 0 ||
      isLoading ||
      isFetching
    ) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    return () => {};
  }, [version, isLoading, isFetching, currentversion]);

  const onVersionUpdate = useCallback((data: AppVersion) => {
    const newdata = data
    setVersion(newdata?.version_number || '');
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAppVersion({ onNext: onVersionUpdate });
    console.log("subscribeAppVersion", unsubscribe);
    return () => unsubscribeAppVersion?.();
  }, [onVersionUpdate, networkStatus]);

  const handleReload = (): void => {
    window.location.reload();
  };
  const handleClose = (): void => {
    setIsVisible(false);
  };

  return {
    currentversion,
    version,
    isLoading,
    isFetching,
    isVisible,
    handleReload,
    handleClose,
  };
};
